/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Scope types for global context switching
export const ScopeTypes = {
  SYSTEM: 'system',
  PORTFOLIO: 'portfolio',
  PLANT: 'plant',
};

// Supported markets/countries
export const Markets = {
  GB: { code: 'GB', name: 'Great Britain', flag: '🇬🇧', currency: 'GBP', timezone: 'Europe/London' },
  DE: { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR', timezone: 'Europe/Berlin' },
  IE: { code: 'IE', name: 'Ireland', flag: '🇮🇪', currency: 'EUR', timezone: 'Europe/Dublin' },
  ALL: { code: 'ALL', name: 'All Markets', flag: '🇪🇺', currency: 'EUR', timezone: 'UTC' },
};

// Initial state
const initialState = {
  // Selected market/country
  selectedMarket: 'GB', // Default to GB for backward compatibility
  // Global scope for data filtering
  scope: {
    type: ScopeTypes.SYSTEM,
    portfolioId: null,
    portfolioName: null,
    plantId: null,
    plantName: null,
  },
  // Selected date for data queries
  selectedDate: new Date().toISOString().split('T')[0],
  // Selected settlement period (1-48)
  selectedPeriod: null,
  // Selected BMU for detailed view
  selectedBMU: null,
  // Fuel type filter
  selectedFuelType: null,
  // Loading states
  loading: {
    generation: false,
    physical: false,
    bidOffer: false,
    system: false,
  },
  // Error states
  errors: {},
  // Cache for API responses
  cache: {},
  // Last data refresh timestamp
  lastUpdated: null,
  // Refresh counter (increment to trigger data refresh)
  refreshCounter: 0,
  // API rate limit tracking
  apiCalls: {
    count: 0,
    resetTime: null,
    limit: 100, // BMRS API limit per minute
  },
  // UI preferences
  preferences: {
    theme: 'dark',
    chartType: 'line',
    autoRefresh: true,
    refreshInterval: 60000, // 1 minute
  },
  // Sidebar state
  sidebarOpen: true,
  // Keyboard shortcuts enabled
  keyboardShortcutsEnabled: true,
};

// Action types
const ActionTypes = {
  SET_MARKET: 'SET_MARKET',
  SET_SCOPE: 'SET_SCOPE',
  SET_DATE: 'SET_DATE',
  SET_PERIOD: 'SET_PERIOD',
  SET_BMU: 'SET_BMU',
  SET_FUEL_TYPE: 'SET_FUEL_TYPE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_CACHE: 'SET_CACHE',
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  RESET_FILTERS: 'RESET_FILTERS',
  SET_LAST_UPDATED: 'SET_LAST_UPDATED',
  TRIGGER_REFRESH: 'TRIGGER_REFRESH',
  INCREMENT_API_CALLS: 'INCREMENT_API_CALLS',
  RESET_API_CALLS: 'RESET_API_CALLS',
  TOGGLE_THEME: 'TOGGLE_THEME',
  TOGGLE_KEYBOARD_SHORTCUTS: 'TOGGLE_KEYBOARD_SHORTCUTS',
};

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_MARKET:
      return { ...state, selectedMarket: action.payload };

    case ActionTypes.SET_SCOPE:
      return { ...state, scope: action.payload };

    case ActionTypes.SET_DATE:
      return { ...state, selectedDate: action.payload };

    case ActionTypes.SET_PERIOD:
      return { ...state, selectedPeriod: action.payload };

    case ActionTypes.SET_BMU:
      return { ...state, selectedBMU: action.payload };

    case ActionTypes.SET_FUEL_TYPE:
      return { ...state, selectedFuelType: action.payload };

    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: { ...state.loading, [action.payload.key]: action.payload.value },
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        errors: { ...state.errors, [action.payload.key]: action.payload.error },
      };

    case ActionTypes.CLEAR_ERROR: {
      const newErrors = { ...state.errors };
      delete newErrors[action.payload];
      return { ...state, errors: newErrors };
    }

    case ActionTypes.SET_CACHE:
      return {
        ...state,
        cache: { ...state.cache, [action.payload.key]: action.payload.data },
      };

    case ActionTypes.UPDATE_PREFERENCES:
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload },
      };

    case ActionTypes.TOGGLE_SIDEBAR:
      return { ...state, sidebarOpen: !state.sidebarOpen };

    case ActionTypes.RESET_FILTERS:
      return {
        ...state,
        selectedDate: new Date().toISOString().split('T')[0],
        selectedPeriod: null,
        selectedBMU: null,
        selectedFuelType: null,
      };

    case ActionTypes.SET_LAST_UPDATED:
      return { ...state, lastUpdated: action.payload };

    case ActionTypes.TRIGGER_REFRESH:
      return {
        ...state,
        refreshCounter: state.refreshCounter + 1,
        lastUpdated: new Date().toISOString(),
      };

    case ActionTypes.INCREMENT_API_CALLS:
      return {
        ...state,
        apiCalls: {
          ...state.apiCalls,
          count: state.apiCalls.count + 1,
        },
      };

    case ActionTypes.RESET_API_CALLS:
      return {
        ...state,
        apiCalls: {
          ...state.apiCalls,
          count: 0,
          resetTime: new Date(Date.now() + 60000).toISOString(),
        },
      };

    case ActionTypes.TOGGLE_THEME:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          theme: state.preferences.theme === 'dark' ? 'light' : 'dark',
        },
      };

    case ActionTypes.TOGGLE_KEYBOARD_SHORTCUTS:
      return {
        ...state,
        keyboardShortcutsEnabled: !state.keyboardShortcutsEnabled,
      };

    default:
      return state;
  }
}

// Create context
const AppContext = createContext(null);

// Provider component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Action creators
  const setMarket = useCallback((marketCode) => {
    dispatch({ type: ActionTypes.SET_MARKET, payload: marketCode });
  }, []);

  const setScope = useCallback((scopeType, id = null, name = null) => {
    const scope = {
      type: scopeType,
      portfolioId: scopeType === ScopeTypes.PORTFOLIO ? id : null,
      portfolioName: scopeType === ScopeTypes.PORTFOLIO ? name : null,
      plantId: scopeType === ScopeTypes.PLANT ? id : null,
      plantName: scopeType === ScopeTypes.PLANT ? name : null,
    };
    dispatch({ type: ActionTypes.SET_SCOPE, payload: scope });
  }, []);

  const setDate = useCallback((date) => {
    dispatch({ type: ActionTypes.SET_DATE, payload: date });
  }, []);

  const setPeriod = useCallback((period) => {
    dispatch({ type: ActionTypes.SET_PERIOD, payload: period });
  }, []);

  const setBMU = useCallback((bmuId) => {
    dispatch({ type: ActionTypes.SET_BMU, payload: bmuId });
  }, []);

  const setFuelType = useCallback((fuelType) => {
    dispatch({ type: ActionTypes.SET_FUEL_TYPE, payload: fuelType });
  }, []);

  const setLoading = useCallback((key, value) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: { key, value } });
  }, []);

  const setError = useCallback((key, error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: { key, error } });
  }, []);

  const clearError = useCallback((key) => {
    dispatch({ type: ActionTypes.CLEAR_ERROR, payload: key });
  }, []);

  const setCache = useCallback((key, data) => {
    dispatch({ type: ActionTypes.SET_CACHE, payload: { key, data } });
  }, []);

  const updatePreferences = useCallback((prefs) => {
    dispatch({ type: ActionTypes.UPDATE_PREFERENCES, payload: prefs });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: ActionTypes.TOGGLE_SIDEBAR });
  }, []);

  const resetFilters = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_FILTERS });
  }, []);

  const setLastUpdated = useCallback((timestamp) => {
    dispatch({ type: ActionTypes.SET_LAST_UPDATED, payload: timestamp });
  }, []);

  const triggerRefresh = useCallback(() => {
    dispatch({ type: ActionTypes.TRIGGER_REFRESH });
  }, []);

  const incrementApiCalls = useCallback(() => {
    dispatch({ type: ActionTypes.INCREMENT_API_CALLS });
  }, []);

  const resetApiCalls = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_API_CALLS });
  }, []);

  const toggleTheme = useCallback(() => {
    dispatch({ type: ActionTypes.TOGGLE_THEME });
  }, []);

  const toggleKeyboardShortcuts = useCallback(() => {
    dispatch({ type: ActionTypes.TOGGLE_KEYBOARD_SHORTCUTS });
  }, []);

  const value = {
    state,
    actions: {
      setMarket,
      setScope,
      setDate,
      setPeriod,
      setBMU,
      setFuelType,
      setLoading,
      setError,
      clearError,
      setCache,
      updatePreferences,
      toggleSidebar,
      resetFilters,
      setLastUpdated,
      triggerRefresh,
      incrementApiCalls,
      resetApiCalls,
      toggleTheme,
      toggleKeyboardShortcuts,
    },
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// Custom hook to use the context
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export default AppContext;
